/**
 * WhatsApp Flow automatic payload + data mapping (builder-only).
 *
 * - Local values on a screen are referenced as ${form.<field>}
 * - Inherited values from previous screens are referenced as ${data.<field>}
 * - navigate.payload auto-passes the accumulated values to the next screen
 * - complete.payload auto-includes the accumulated values as final submission payload
 */

const INPUT_TYPE_TO_DATA_TYPE = {
  TextInput: "string",
  Dropdown: "string",
  RadioButtonsGroup: "string",
  CheckboxGroup: "array",
  DatePicker: "string",
};
const INPUT_TYPES = new Set(Object.keys(INPUT_TYPE_TO_DATA_TYPE));

function getDataExampleForType(type) {
  if (type === "number") return 0;
  if (type === "boolean") return false;
  if (type === "array") return [];
  return "";
}

function isObject(x) {
  return !!x && typeof x === "object" && !Array.isArray(x);
}

function getNamedComponentsOnScreen(screen) {
  const out = [];
  const layoutChildren = screen?.layout?.children || [];
  for (const child of layoutChildren) {
    if (child?.name && INPUT_TYPES.has(child.type)) out.push(child);
    if (child?.type === "Form") {
      for (const fc of child.children || []) {
        if (fc?.name && INPUT_TYPES.has(fc.type)) out.push(fc);
      }
    }
  }
  return out;
}

function getLocalFieldType(comp) {
  if (!comp) return "string";
  if (INPUT_TYPE_TO_DATA_TYPE[comp.type]) return INPUT_TYPE_TO_DATA_TYPE[comp.type];
  return "string";
}

function ensureActionShape(footer) {
  const action = footer["on-click-action"] || {};
  if (action.name === "complete") {
    return { name: "complete" };
  }
  // default navigate shape
  const nextName = action.next?.name || "";
  return { name: "navigate", next: { type: "screen", name: nextName } };
}

/**
 * Computes per-screen derived data:
 * - incomingDataSpec: what should exist as screen.data (keys accumulated from previous screens)
 * - availableTokens: strings for dropdown insertions on this screen
 * - footerPayloadForAction: auto payload object for navigate/complete on this screen
 */
export function computeAutoMapping(flow) {
  const screens = flow?.screens || [];
  const screenOrder = screens.map((s) => s.id);
  const byId = new Map(screens.map((s) => [s.id, s]));

  const accumulated = new Map(); // key -> type
  const perScreen = new Map(); // screenId -> meta

  for (let i = 0; i < screens.length; i++) {
    const screen = screens[i];
    const screenId = screen?.id;
    if (!screenId) continue;

    // Incoming data spec is accumulated BEFORE this screen's local fields are added.
    const incomingDataSpec = {};
    for (const [k, t] of accumulated.entries()) {
      incomingDataSpec[k] = { type: t, __example__: getDataExampleForType(t) };
    }

    // Local fields on this screen
    const locals = getNamedComponentsOnScreen(screen);
    const localKeys = new Set();
    locals.forEach((c) => {
      localKeys.add(c.name);
    });

    // Tokens available on this screen
    const tokens = [
      ...Array.from(accumulated.keys()).map((k) => `\${data.${k}}`),
      ...Array.from(localKeys).map((k) => `\${form.${k}}`),
    ];

    // Payload builder uses ${data.*} for inherited keys and ${form.*} for local keys
    const payloadAll = {};
    for (const [k] of accumulated.entries()) payloadAll[k] = `\${data.${k}}`;
    for (const k of localKeys) payloadAll[k] = `\${form.${k}}`;

    perScreen.set(screenId, {
      idx: i,
      incomingDataSpec,
      localKeys,
      tokens,
      payloadAll,
      nextScreenIds: screenOrder.filter((id) => id && id !== screenId),
    });

    // Update accumulated after processing this screen
    locals.forEach((c) => {
      accumulated.set(c.name, getLocalFieldType(c));
    });
  }

  return { perScreen, byId };
}

export function applyAutoMappingToFlow(flow) {
  if (!flow || !Array.isArray(flow.screens)) return flow;
  const { perScreen } = computeAutoMapping(flow);
  const screens = flow.screens.map((s) => {
    const meta = perScreen.get(s.id);
    if (!meta) return s;

    const nextScreen = { ...s };
    // Set screen.data to incoming (inherited) contract
    nextScreen.data = meta.incomingDataSpec;

    // Patch footers with auto payload
    const layoutChildren = nextScreen.layout?.children || [];
    const newLayoutChildren = layoutChildren.map((child) => {
      if (child.type === "Footer") {
        const baseAction = ensureActionShape(child);
        return {
          ...child,
          "on-click-action": {
            ...baseAction,
            payload: isObject(meta.payloadAll) ? meta.payloadAll : {},
          },
        };
      }
      if (child.type === "Form") {
        const newFormChildren = (child.children || []).map((fc) => {
          if (fc.type !== "Footer") return fc;
          const baseAction = ensureActionShape(fc);
          return {
            ...fc,
            "on-click-action": {
              ...baseAction,
              payload: isObject(meta.payloadAll) ? meta.payloadAll : {},
            },
          };
        });
        return { ...child, children: newFormChildren };
      }
      return child;
    });

    nextScreen.layout = { ...nextScreen.layout, children: newLayoutChildren };
    return nextScreen;
  });

  return { ...flow, screens };
}

export function getFooterAutoPayload(flow, screenId) {
  const { perScreen } = computeAutoMapping(flow);
  return perScreen.get(screenId)?.payloadAll || {};
}

export function getScreenAutoTokens(flow, screenId) {
  const { perScreen } = computeAutoMapping(flow);
  return perScreen.get(screenId)?.tokens || [];
}

